import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Lock,
  Unlock,
  Heart,
  Users,
  TrendingUp,
  Calendar,
  Gift,
  Shield,
  Sparkles,
  Check,
  Loader2,
  Eye,
  ScrollText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CoffreFort = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [charterStatus, setCharterStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [donating, setDonating] = useState(false);
  
  // Donation form
  const [donationAmount, setDonationAmount] = useState(5);
  const [donationMessage, setDonationMessage] = useState('');
  const [donationAnonymous, setDonationAnonymous] = useState(false);
  const [showDonationForm, setShowDonationForm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('tkale_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, contribRes, charterRes] = await Promise.all([
        axios.get(`${API}/strongbox/stats`),
        axios.get(`${API}/strongbox/contributions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/strongbox/charter/status`, { headers }).catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setContributions(contribRes.data);
      setCharterStatus(charterRes.data);
    } catch (error) {
      console.error('Error loading strongbox data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.post(
        `${API}/strongbox/subscribe?plan=${plan}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'abonnement');
    } finally {
      setSubscribing(false);
    }
  };

  const handleDonate = async () => {
    if (donationAmount < 1) {
      toast.error('Le montant minimum est de 1€');
      return;
    }
    
    setDonating(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.post(
        `${API}/strongbox/donate`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: {
            amount: donationAmount,
            message: donationMessage || null,
            anonymous: donationAnonymous
          }
        }
      );
      toast.success(response.data.message);
      setShowDonationForm(false);
      setDonationAmount(5);
      setDonationMessage('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la donation');
    } finally {
      setDonating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-950 via-slate-900 to-amber-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
          <p className="text-amber-300/70">Ouverture du Coffre Fort...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-950 via-slate-900 to-amber-950" data-testid="coffre-fort">
      
      {/* Header */}
      <header className="border-b border-amber-800/50 bg-amber-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-amber-300 hover:text-amber-100"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-400" />
                  Coffre Fort
                </h1>
                <p className="text-sm text-amber-400/70">
                  Transparence totale • Chaque euro compte
                </p>
              </div>
            </div>
            {charterStatus?.has_signed_charter && (
              <Badge className="bg-green-600/20 text-green-300 border border-green-500/50">
                <Check className="w-4 h-4 mr-1" />
                Charte signée
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-amber-900/30 border-amber-700/50">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-6 h-6 text-amber-400" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">
                  {stats?.total_amount?.toFixed(2) || '0.00'} €
                </p>
                <p className="text-amber-400/70 text-sm">Total du Coffre</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-blue-900/30 border-blue-700/50">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">
                  {stats?.active_subscribers || 0}
                </p>
                <p className="text-blue-400/70 text-sm">Abonnés actifs</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-pink-900/30 border-pink-700/50">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-pink-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Heart className="w-6 h-6 text-pink-400" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">
                  {stats?.total_donations?.toFixed(2) || '0.00'} €
                </p>
                <p className="text-pink-400/70 text-sm">Donations</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-green-900/30 border-green-700/50">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">
                  {stats?.total_contributions || 0}
                </p>
                <p className="text-green-400/70 text-sm">Contributions</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          
          {/* Subscription Options */}
          <div className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Unlock className="w-5 h-5 text-amber-400" />
                  Accès au Portail
                </CardTitle>
                <CardDescription>
                  Votre contribution alimente le Coffre Fort et finance les projets culturels
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                
                {/* Monthly */}
                <div 
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    charterStatus?.subscription_type === 'monthly' 
                      ? 'bg-amber-900/30 border-amber-500' 
                      : 'bg-slate-900/30 border-slate-700 hover:border-amber-600/50'
                  }`}
                  onClick={() => !subscribing && handleSubscribe('monthly')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">Mensuel</p>
                      <p className="text-slate-400 text-sm">Flexibilité totale</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-amber-400">1 €</p>
                      <p className="text-slate-500 text-xs">/ mois</p>
                    </div>
                  </div>
                </div>

                {/* Yearly */}
                <div 
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer relative ${
                    charterStatus?.subscription_type === 'yearly' 
                      ? 'bg-green-900/30 border-green-500' 
                      : 'bg-slate-900/30 border-slate-700 hover:border-green-600/50'
                  }`}
                  onClick={() => !subscribing && handleSubscribe('yearly')}
                >
                  <Badge className="absolute -top-2 right-4 bg-green-600 text-white text-xs">
                    2 mois offerts
                  </Badge>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">Annuel</p>
                      <p className="text-slate-400 text-sm">Engagement solidaire</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-400">10 €</p>
                      <p className="text-slate-500 text-xs">/ an</p>
                    </div>
                  </div>
                </div>

                {subscribing && (
                  <div className="text-center py-2">
                    <Loader2 className="w-6 h-6 text-amber-400 animate-spin mx-auto" />
                  </div>
                )}

                {charterStatus?.subscription_active && (
                  <div className="p-3 bg-green-900/20 rounded-lg border border-green-700/50">
                    <p className="text-green-300 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Abonnement actif jusqu'au {new Date(charterStatus.subscription_end).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Donation */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Gift className="w-5 h-5 text-pink-400" />
                  Donation libre
                </CardTitle>
                <CardDescription>
                  Soutenez les projets culturels et l'aide aux communautés
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!showDonationForm ? (
                  <Button 
                    className="w-full bg-gradient-to-r from-pink-600 to-rose-600"
                    onClick={() => setShowDonationForm(true)}
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Faire une donation
                  </Button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-4"
                  >
                    <div>
                      <Label className="text-slate-300">Montant (€)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={donationAmount}
                        onChange={(e) => setDonationAmount(parseFloat(e.target.value) || 0)}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Message (optionnel)</Label>
                      <Input
                        value={donationMessage}
                        onChange={(e) => setDonationMessage(e.target.value)}
                        placeholder="Un mot pour accompagner votre don..."
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={donationAnonymous}
                        onCheckedChange={setDonationAnonymous}
                      />
                      <Label className="text-slate-400 text-sm">Donation anonyme</Label>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1 border-slate-600"
                        onClick={() => setShowDonationForm(false)}
                      >
                        Annuler
                      </Button>
                      <Button 
                        className="flex-1 bg-pink-600"
                        onClick={handleDonate}
                        disabled={donating}
                      >
                        {donating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Contributions & Charter */}
          <div className="space-y-6">
            
            {/* Charter Status */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ScrollText className="w-5 h-5 text-amber-400" />
                  Charte de la Graine
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-amber-900/20 rounded-lg border border-amber-700/30 mb-4">
                  <p className="text-amber-200/80 text-sm italic">
                    "La Charte accompagne, elle ne contrôle pas. Elle sera dévoilée prochainement, 
                    une fois les protections en place. Elle est la lumière même."
                  </p>
                </div>
                
                {charterStatus?.has_signed_charter ? (
                  <div className="p-3 bg-green-900/20 rounded-lg border border-green-700/50">
                    <p className="text-green-300 text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Vous avez signé la Charte le {new Date(charterStatus.charter_signed_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                ) : (
                  <Button 
                    className="w-full bg-amber-600 hover:bg-amber-500"
                    onClick={() => navigate('/charter')}
                  >
                    <ScrollText className="w-4 h-4 mr-2" />
                    Lire et signer la Charte
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Recent Contributions */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-cyan-400" />
                  Contributions récentes
                </CardTitle>
                <CardDescription>
                  Transparence totale • Chaque contribution visible
                </CardDescription>
              </CardHeader>
              <CardContent>
                {contributions.length === 0 ? (
                  <div className="text-center py-6">
                    <Sparkles className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-500">Soyez le premier à contribuer !</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {contributions.map((contrib, index) => (
                      <motion.div
                        key={contrib.contribution_id || index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            contrib.type === 'donation' 
                              ? 'bg-pink-600/20' 
                              : 'bg-amber-600/20'
                          }`}>
                            {contrib.type === 'donation' 
                              ? <Heart className="w-4 h-4 text-pink-400" />
                              : <Calendar className="w-4 h-4 text-amber-400" />
                            }
                          </div>
                          <div>
                            <p className="text-white text-sm">{contrib.user_email}</p>
                            <p className="text-slate-500 text-xs">
                              {contrib.type === 'donation' ? 'Donation' : 
                               contrib.type === 'subscription_yearly' ? 'Abonnement annuel' : 'Abonnement mensuel'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-amber-400 font-medium">{contrib.amount?.toFixed(2)} €</p>
                          <p className="text-slate-500 text-xs">
                            {new Date(contrib.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            100% des contributions vont dans le Coffre Fort • Aucune marge cachée • Transparence totale
          </p>
        </div>
      </main>
    </div>
  );
};

export default CoffreFort;
